import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Children, useCallback, useEffect, useRef, useState, type ReactNode } from 'react';

import { cn } from '../../lib/cn';
import { Icon } from '../Icon/Icon';

/**
 * docs/02-DESIGN-SYSTEM.md §4.2 — "Scroll-snap row + circular ‹ › controls;
 * keyboard and swipe support."
 *
 * Built on native overflow scrolling with CSS scroll-snap rather than a
 * transform-driven slider. That gives touch swipe, momentum, trackpad gestures
 * and arrow-key scrolling for free, all of which a JS slider has to
 * reimplement — and it degrades to a plain scrollable row if the script never
 * runs, which matters for the prerendered pages in §5.1 of the frontend spec.
 *
 * The track is focusable on purpose: a scrollable region that cannot be
 * reached by keyboard is a WCAG failure (axe's `scrollable-region-focusable`),
 * and the ‹ › buttons alone do not fix it.
 *
 * Not an auto-advancing carousel. Nothing in the mockups rotates on its own,
 * and §6 disables motion under `prefers-reduced-motion` anyway.
 */
export interface CarouselLabels {
  /** Names the carousel region, e.g. "სტუდენტური კლუბები". */
  region: string;
  previous: string;
  next: string;
}

export interface CarouselProps {
  labels: CarouselLabels;
  children: ReactNode;
  /** Tailwind classes for each item's width, e.g. "w-72 sm:w-80". */
  itemClassName?: string;
  className?: string;
}

export function Carousel({ labels, children, itemClassName, className }: CarouselProps) {
  const trackRef = useRef<HTMLUListElement>(null);
  const [atStart, setAtStart] = useState(true);
  const [atEnd, setAtEnd] = useState(false);

  const syncEdges = useCallback(() => {
    const track = trackRef.current;

    if (!track) {
      return;
    }

    const { scrollLeft, scrollWidth, clientWidth } = track;
    // A pixel of tolerance: fractional scroll positions are normal at
    // non-integer zoom levels and would otherwise leave "next" enabled at the
    // very end.
    setAtStart(scrollLeft <= 1);
    setAtEnd(scrollLeft + clientWidth >= scrollWidth - 1);
  }, []);

  useEffect(() => {
    syncEdges();

    const track = trackRef.current;

    if (!track) {
      return;
    }

    track.addEventListener('scroll', syncEdges, { passive: true });
    window.addEventListener('resize', syncEdges);

    return () => {
      track.removeEventListener('scroll', syncEdges);
      window.removeEventListener('resize', syncEdges);
    };
  }, [syncEdges, children]);

  const scrollByPage = useCallback((direction: 1 | -1) => {
    const track = trackRef.current;

    if (!track) {
      return;
    }

    // Advance by one item where we can measure one, otherwise by a viewport.
    const firstItem = track.firstElementChild as HTMLElement | null;
    const step = firstItem ? firstItem.offsetWidth + 24 : track.clientWidth;

    track.scrollBy({ left: step * direction, behavior: 'smooth' });
  }, []);

  return (
    <section
      aria-roledescription="carousel"
      aria-label={labels.region}
      className={cn('relative', className)}
    >
      <ul
        ref={trackRef}
        /*
         * jsx-a11y objects to tabIndex on a non-interactive element, and here
         * it is wrong to. A region that scrolls must be keyboard-reachable or
         * a keyboard user cannot scroll it — that is axe's
         * `scrollable-region-focusable` rule, which fails at `serious` and
         * would block the build under §7.10. The ‹ › buttons do not satisfy it
         * because the track scrolls independently of them.
         *
         * The two rules genuinely disagree; axe is the one §7.10 enforces.
         */
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0}
        className={cn(
          'flex snap-x snap-mandatory gap-6 overflow-x-auto scroll-smooth pb-4',
          // The scrollbar is hidden visually; the row remains scrollable and
          // focusable, and the ‹ › controls duplicate the affordance.
          '[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        )}
      >
        {/*
          Normalised rather than branching on Array.isArray: a single child is
          not an array, and rendering it straight into the <ul> put a non-<li>
          element in a list, which axe fails at `serious`.
        */}
        {Children.toArray(children).map((child, index) => (
          <li key={index} className={cn('shrink-0 snap-start', itemClassName)}>
            {child}
          </li>
        ))}
      </ul>

      <div className="mt-4 flex justify-end gap-2">
        <button
          type="button"
          aria-label={labels.previous}
          disabled={atStart}
          onClick={() => {
            scrollByPage(-1);
          }}
          className={CONTROL_CLASSES}
        >
          <Icon as={ChevronLeft} />
        </button>
        <button
          type="button"
          aria-label={labels.next}
          disabled={atEnd}
          onClick={() => {
            scrollByPage(1);
          }}
          className={CONTROL_CLASSES}
        >
          <Icon as={ChevronRight} />
        </button>
      </div>
    </section>
  );
}

const CONTROL_CLASSES = cn(
  'flex h-11 w-11 items-center justify-center rounded-full',
  'border border-strong bg-surface text-primary',
  'transition-colors duration-fast hover:border-navy-600 hover:text-navy-600',
  'disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-strong disabled:hover:text-primary',
);
